import * as Sentry from '@sentry/browser';

let sentryConfigured = false;

const shouldIgnore = (exception) => {
    if (!exception) {
        return true;
    }

    const data = (JSON.stringify(exception) || '').toLowerCase();

    if (data.indexOf('play() request was interrupted') !== -1) {
        return true;
    }

    if (data.indexOf('loading chunk') !== -1) {
        return true;
    }

    return false;
};

const shouldShowDialog = (exception) => {
    return exception?.extra?.showDialog === true;
};

const configureSentry = ({dsn}) => {
    Sentry.init({
        dsn,
        integrations: [Sentry.browserTracingIntegration()],
        beforeSend(event, {originalException}) {
            // Custom messages do not have to be exceptions.
            if (!event.exception) {
                return event;
            }

            if (shouldIgnore(originalException)) {
                return null;
            }

            if (shouldShowDialog(event)) {
                Sentry.showReportDialog({eventId: event.event_id});
            }

            return event;
        },
        ignoreErrors: [
            'top.GLOBALS',
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'http://tt.epicplay.com',
            'Can\'t find variable: ZiteReader',
            'jigsaw is not defined',
            'ComboSearch is not defined',
            'http://loading.retry.widdit.com/',
            'atomicFindClose',
            'fb_xd_fragment',
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            'conduitPage',
            'Script error.',
            '_avast_submit',
        ],
        denyUrls: [
            /pagead\/js/i,
            /graph\.facebook\.com/i,
            /connect\.facebook\.net\/en_US\/all\.js/i,
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /127\.0\.0\.1:4001\/isrunning/i,
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
    });

    sentryConfigured = true;
};

const emitDataLayer = (exception, category = 'Errors', action = 'Unhandled Exception') => {
    window.dataLayer = window.dataLayer || [];

    const eventData = {
        event: 'CraftEvent',
        eventCategory: category,
        eventAction: action,
        eventLabel: exception,
    };

    window.dataLayer.push(eventData);
};

const captureException = (exception, showDialog = false) => {
    const message = exception && exception.message ? exception.message : 'Unknown Error';

    emitDataLayer(message);

    if (!sentryConfigured) {
        return message;
    }

    let {extra, tags, contexts, user, level, fingerprint} = exception;

    if (!extra) {
        extra = {};
    }

    extra.showDialog = showDialog;

    Sentry.captureException(exception, {extra, tags, contexts, user, level, fingerprint});
};

const captureMessage = (message, category = 'Errors', action = 'Message') => {
    emitDataLayer(message, category, action);

    if (!sentryConfigured) {
        return message;
    }

    Sentry.captureMessage(message);
};

const captureValidationMessage = (message, action = 'Handled Exception') => {
    return captureMessage(message, 'Validation Errors', action);
};

export {Sentry, emitDataLayer, captureMessage, configureSentry, captureValidationMessage};

export default captureException;

window.captureMessage = captureMessage;
window.captureException = captureException;
